
import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/context/AuthContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { ThemeProvider } from "@/context/ThemeContext"; 
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GlobalAdBannerBelowHeader from "@/components/GlobalAdBannerBelowHeader";

import PixPromotionModal from "@/components/PixPromotionModal";
import PixFloatingButton from "@/components/PixFloatingButton";
import GuruFloatingButton from "@/components/guru-chat/GuruFloatingButton";
import WhatsNewBalloon from "@/components/WhatsNewBalloon";
import FreeUserOfferBanner from "@/components/FreeUserOfferBanner";
import ExpiredPlanBanner from "@/components/ExpiredPlanBanner";
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import GerarJogosPage from "@/pages/GerarJogosPage";
import LoteriasPage from "@/pages/LoteriasPage";
import SoftwarePage from "@/pages/SoftwarePage";
import ResultadosBicho from "@/pages/ResultadosBicho";
import EstatisticasPage from "@/pages/EstatisticasPage";

import MyAccountPage from "@/pages/MyAccountPage";
import PlansPage from "@/pages/PlansPage";
import AdminPage from "@/pages/AdminPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AffiliatePage from "@/pages/AffiliatePage";
import SupportPage from "@/pages/SupportPage";
import FAQPage from "@/pages/FAQPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import PuxadasPage from "@/pages/PuxadasPage";
import MetodosPage from "@/pages/MetodosPage";
import InstalarPage from "@/pages/InstalarPage";
import LojaPage from "@/pages/LojaPage";
import PagamentoPage from "@/pages/PagamentoPage";
import PalpitesShowPage from "@/pages/PalpitesShowPage";
import VitrinePage from "@/pages/VitrinePage";
import GlobalVitrineBanner from "@/components/vitrine/GlobalVitrineBanner";
import SobreNosPage from "@/pages/SobreNosPage";
import UsoResponsavelPage from "@/pages/UsoResponsavelPage";
import GuruChatPage from "@/pages/GuruChatPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ConfirmarEmailPage from "@/pages/ConfirmarEmailPage";
import NotFound from "@/pages/NotFound";
import ProtectedRoute from "@/components/ProtectedRoute";
import AffiliateTrackingWrapper from "@/components/AffiliateTrackingWrapper";
import InstallPrompt from "@/components/InstallPrompt";
import MobileBottomNav from "@/components/MobileBottomNav";
import UpgradeModal from "@/components/UpgradeModal";
import { useUpgradeModal } from "@/hooks/useUpgradeModal";
import PlayStoreDisclaimerModal from "@/components/legal/PlayStoreDisclaimerModal";
import PlayStoreFooterNotice from "@/components/legal/PlayStoreFooterNotice";
import { isAndroidTWA } from "@/utils/runtimeContext";
import AnalyticsProvider from "@/components/AnalyticsProvider";
import "./App.css";

const queryClient = new QueryClient();

function AppContent() {
  const { showUpgradeModal, closeUpgradeModal } = useUpgradeModal();
  const inTWA = isAndroidTWA();

  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground">
      <AffiliateTrackingWrapper />
      <AnalyticsProvider />
      <InstallPrompt />
      <Header />
      <GlobalVitrineBanner />
      <GlobalAdBannerBelowHeader />
      <ExpiredPlanBanner />
      {!inTWA && <FreeUserOfferBanner />}
      <main className="flex-1 w-full pb-20 md:pb-0">
        <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/cadastro" element={<RegisterPage />} />
                    <Route 
                      path="/dashboard" 
                      element={
                        <ProtectedRoute>
                          <DashboardPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/gerar-jogos" 
                      element={
                        <ProtectedRoute>
                          <GerarJogosPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/loterias/:lotteryType?" 
                      element={
                        <ProtectedRoute>
                          <LoteriasPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/software" 
                      element={
                        <ProtectedRoute>
                          <SoftwarePage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route path="/resultados-bicho" element={<ResultadosBicho />} />
                    <Route path="/estatisticas" element={
                      <ProtectedRoute>
                        <EstatisticasPage />
                      </ProtectedRoute>
                    } />
                    <Route 
                      path="/minha-conta" 
                      element={
                        <ProtectedRoute>
                          <MyAccountPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route path="/planos" element={<PlansPage />} />
                    <Route 
                      path="/admin" 
                      element={
                        <ProtectedRoute requireAdmin>
                          <AdminPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/admin/dashboard" 
                      element={
                        <ProtectedRoute requireAdmin>
                          <AdminDashboardPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/admin/palpites-show" 
                      element={
                        <ProtectedRoute requireAdmin>
                          <PalpitesShowPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/afiliados" 
                      element={
                        <ProtectedRoute>
                          <AffiliatePage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route path="/suporte" element={<SupportPage />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/instalar" element={<InstalarPage />} />
                    <Route path="/payment/success" element={<PaymentSuccessPage />} />
                    <Route path="/payment/cancel" element={<PaymentCancelPage />} />
                    <Route path="/auth/callback" element={<AuthCallbackPage />} />
                    <Route 
                      path="/puxadas" 
                      element={
                        <ProtectedRoute>
                          <PuxadasPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/metodos" 
                      element={
                        <ProtectedRoute>
                          <MetodosPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route path="/loja" element={<LojaPage />} />
                    <Route path="/vitrine" element={<VitrinePage />} />
                    <Route path="/pagamento" element={<PagamentoPage />} />
                    <Route path="/sobre-nos" element={<SobreNosPage />} />
                    <Route path="/uso-responsavel" element={<UsoResponsavelPage />} />
                    <Route path="/guru-chat" element={<GuruChatPage />} />
                    <Route path="/guru-chat/:threadId" element={<GuruChatPage />} />
                    <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/confirmar-email" element={<ConfirmarEmailPage />} />
                    <Route path="*" element={<NotFound />} />
                    <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <PlayStoreFooterNotice />
      <MobileBottomNav />
      <Toaster />
      <Sonner />
      {!inTWA && <PixPromotionModal />}
      {!inTWA && <PixFloatingButton />}
      <GuruFloatingButton />
      {/* standby: WhatsNewBalloon desativado temporariamente para não cobrir o botão de Suporte/WhatsApp no mobile */}
      {/* <WhatsNewBalloon /> */}
      {!inTWA && (
        <UpgradeModal
          open={showUpgradeModal}
          onClose={closeUpgradeModal}
        />
      )}
      {/* Modo Play Store desativado em 15/06/2026 — modal +18 removido do fluxo.
          Componente preservado em src/components/legal/PlayStoreDisclaimerModal.tsx.
          Para religar: descomentar a linha abaixo.
          {inTWA && <PlayStoreDisclaimerModal />} */}

    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <NotificationProvider>
              <Router>
                <AppContent />
              </Router>
            </NotificationProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
